import { auth } from '../config/firebase';
import { getSelectedUniverseId } from '../store/universeStore';
import { HttpError } from './httpError';

export { HttpError };

/**
 * POST to a same-origin Cloud Function endpoint (`/tickPlanet`, etc.) with
 * the caller's Firebase ID token and the current session's `universeId`
 * stamped onto the body (issue #53) — see `requireUniverseId` in
 * `apps/functions/src/index.ts`. Previously duplicated verbatim across six
 * service files; extracted here per the single-source-of-truth convention
 * (see the issue #53 PR review). A caller-supplied `universeId` in `body`
 * wins over the session's pick, since it's spread last.
 *
 * `replayOnly` marks a request that only advances state to now (a tick), so
 * it does not move `requestTransitionCount`.
 */
export async function authedPost<T>(path: string, body: unknown, options: { replayOnly?: boolean } = {}): Promise<T> {
    if (options.replayOnly) return post<T>(path, body);
    requestTransitions++;
    try {
        return await post<T>(path, body);
    } finally {
        requestTransitions++;
    }
}

/**
 * How long a request may take, token refresh included, before it is abandoned.
 * Set past Hosting's fixed 60 s limit on rewritten requests (every endpoint is
 * a `run` rewrite to `api`, which also keeps the function's default 60 s
 * timeout), so a request is only abandoned once the server side has given up
 * on it too, and no endpoint can legitimately answer later. A connection that
 * hangs without a reset never settles on its own, and everything waiting on
 * it (the tick spinner, the game loop's in-flight latch) would stay stuck.
 */
export const REQUEST_TIMEOUT_MS = 70_000;

let requestTransitions = 0;

/**
 * Bumped whenever a request other than a replay starts or settles. A caller
 * that reads the same value twice knows no such request (and so no mutation)
 * began or landed in between — what `tickPlanet` checks before sharing an
 * in-flight tick.
 */
export function requestTransitionCount(): number {
    return requestTransitions;
}

async function post<T>(path: string, body: unknown): Promise<T> {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    try {
        const token = await untilAborted(auth.currentUser!.getIdToken(), controller.signal);
        const res = await fetch(path, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            // TODO: Remove universeId setup from here as a violation of "Separation of concerns"
            body: JSON.stringify({ universeId: getSelectedUniverseId(), ...(body as object) }),
            signal: controller.signal,
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({ error: res.statusText })) as { error?: string };
            throw new HttpError(err.error ?? `Request to ${path} failed: ${res.status}`, res.status);
        }
        return await (res.json() as Promise<T>);
    } catch (err) {
        if (controller.signal.aborted && (err as Error)?.name === 'AbortError') {
            throw new Error(`Request to ${path} timed out after ${REQUEST_TIMEOUT_MS / 1000} s`);
        }
        throw err;
    } finally {
        clearTimeout(timeout);
    }
}

/** `fetch` stops at the signal by itself; anything else awaited inside the deadline needs this. */
function untilAborted<T>(promise: Promise<T>, signal: AbortSignal): Promise<T> {
    return new Promise((resolve, reject) => {
        signal.addEventListener('abort', () => reject(signal.reason), { once: true });
        promise.then(resolve, reject);
    });
}
